import { useState, type FormEvent } from "react";
import { appsApi, type App, type CreateAppInput, type Environment } from "../../api/apps.api";

const ENVS: Environment[] = ["DEVELOPMENT", "STAGING", "PRODUCTION"];

interface Props {
  initial?: App;
  submitLabel: string;
  onSubmit: (input: CreateAppInput) => Promise<void>;
  onCancel: () => void;
}

export function AppForm({ initial, submitLabel, onSubmit, onCancel }: Props) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [url, setUrl] = useState(initial?.url ?? "");
  const [iconUrl, setIconUrl] = useState(initial?.iconUrl ?? "");
  const [logoUrl, setLogoUrl] = useState(initial?.logoUrl ?? "");
  const [environment, setEnvironment] = useState<Environment>(
    initial?.environment ?? "PRODUCTION",
  );
  const [isDefault, setIsDefault] = useState(initial?.isDefault ?? false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [detecting, setDetecting] = useState(false);

  async function handleDetect() {
    if (!initial) return;
    setDetecting(true);
    setError(null);
    try {
      const { iconUrl: detected } = await appsApi.detectIcon(initial.id);
      setIconUrl(detected);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? "Couldn't detect favicon";
      setError(msg);
    } finally {
      setDetecting(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        name,
        description: description || undefined,
        url,
        iconUrl: iconUrl || undefined,
        logoUrl: logoUrl || undefined,
        environment,
        isDefault,
      });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? "Save failed";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="card" onSubmit={handleSubmit}>
      <div className="form-grid">
        <div>
          <label htmlFor="name">Name</label>
          <input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label htmlFor="url">URL</label>
          <input
            id="url"
            type="url"
            placeholder="https://app.example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="logoUrl">Logo URL (optional)</label>
          <input
            id="logoUrl"
            type="url"
            placeholder="https://dash.swfic.net/icons/my-app.svg"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
          />
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
            Shown as the tile image on the dashboard. Takes priority over the favicon.
          </div>
        </div>
        <div>
          <label htmlFor="iconUrl">Favicon URL (optional)</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              id="iconUrl"
              type="url"
              placeholder="https://app.example.com/favicon.ico"
              value={iconUrl}
              onChange={(e) => setIconUrl(e.target.value)}
              style={{ flex: 1 }}
            />
            {initial && (
              <button
                type="button"
                onClick={handleDetect}
                disabled={detecting || submitting}
                title="Fetch the app's HTML and read its <link rel='icon'> href"
              >
                {detecting ? "Detecting…" : "Detect"}
              </button>
            )}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
            Auto-detectable from the app URL. Used as fallback when no logo is set.
          </div>
        </div>
        <div>
          <label htmlFor="environment">Environment</label>
          <select
            id="environment"
            value={environment}
            onChange={(e) => setEnvironment(e.target.value as Environment)}
          >
            {ENVS.map((env) => (
              <option key={env} value={env}>{env}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          <span>
            Grant to new users by default
            <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 400 }}>
              Pre-provisioned users will automatically get access to this app
            </div>
          </span>
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="submit" className="primary" disabled={submitting}>
            {submitting ? "Saving…" : submitLabel}
          </button>
          <button type="button" onClick={onCancel}>Cancel</button>
        </div>
        {error && <div className="error-text">{error}</div>}
      </div>
    </form>
  );
}
